import {
	ErrorModal,
	useContextMenuMESF,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { EdgerRoll } from "@/pages/catalogs/models/EdgerRoll";
import { deleteEdgerRoll } from "@/pages/catalogs/repositories/EdgerRollRepository";
import { ActiveCarrierModal } from "@/pages/catalogs/shared/components/ActiveCarrierModal";
import { CatalogsActionsHeader } from "@/pages/catalogs/shared/components/CatalogsActionsHeader";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { MultipleDeleteSelectModal } from "@/pages/catalogs/shared/components/MultipleDeleteSelectModal";
import { RollsetDeactivationConfirmModal } from "@/pages/catalogs/shared/components/RollsetDeactivationConfirmModal";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { themeDXT } from "@/setup/ag-grid-license";
import { routeEdgerRoll } from "@/setup/routes/catalog.routes";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEdgerRolls } from "../EdgerRollPage";
import { useEdgerRollOptionsFunctions } from "../hooks/useEdgerRollOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { EdgerRollDetails } from "./EdgerRollDetails";

export const TableEdgerRoll = ({ partId }: { partId: string | undefined }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<EdgerRoll | null>(
		null,
	);
	const [selectedRows, setSelectedRows] = useState<EdgerRoll[]>([]);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		| "create"
		| "update"
		| "delete"
		| "deleteMultiple"
		| "confirmRollsetDeactivation"
		| "activeCarrier"
		| ""
	>("");
	const [error, setError] = useState("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("edger-roll-table-panel-sizes");
	const [message, setMessage] = useState("");

	const { data: rows, isLoading } = useEdgerRolls(undefined, (error) =>
		setError(error.message),
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useEdgerRollOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const selectedNodes = gridApi?.getSelectedNodes() || [];
		const selectedData = selectedNodes.map((node) => node.data as EdgerRoll);
		setSelectedRows(selectedData);
	};

	const onRowClicked = (event: RowClickedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.data) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`${routeEdgerRoll}/${(event.data as EdgerRoll).PartId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			const selectedRows = params.api?.getSelectedRows();
			showContextMenu(
				event as any,
				{ ...data, selectedRows: selectedRows },
				"edgerRollContext",
			);
			if (data && selectedRows.length <= 1) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				navigate(`${routeEdgerRoll}/${entityId}`);
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleClearSelection = () => {
		gridApi?.deselectAll();
		setSelectedRows([]);
	};

	const handleNewRoll = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${routeEdgerRoll}/new`);
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const now = new Date();
			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, "0");
			const day = String(now.getDate()).padStart(2, "0");
			const formattedDate = `${year}-${month}-${day}`;

			const columnKeys = gridApi
				.getColumnDefs()
				?.filter(
					(col): col is ColDef =>
						"field" in col && typeof col.field === "string",
				)
				.filter((col) => col.field !== "-")
				.map((col) => col.field)
				.filter((field): field is string => typeof field === "string");

			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `edger_rolls_${formattedDate}.xlsx`,
				columnKeys: columnKeys,
				sheetName: "Edger Rolls",
				allColumns: false,
			};

			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") == "M2.GetEdgerRolls"
			) {
				await queryClient.invalidateQueries({ queryKey: ["edgerRolls"] });
			}
		},
	});

	useEffect(() => {
		if (!selectedRowData || !rows) return;
		const refreshedEdgerRoll = rows.find(
			(d) => d.PartId === selectedRowData.PartId,
		);
		if (refreshedEdgerRoll && refreshedEdgerRoll !== selectedRowData) {
			setSelectedRowData(refreshedEdgerRoll);
		}
	}, [rows]);

	useEffect(() => {
		if (isLoading || !gridApi || partId !== "new") return;

		handleNewRoll();
	}, [isLoading, gridApi, partId]);

	useEffect(() => {
		if (isLoading || !gridApi || !rows || !partId || partId === "new") return;

		const partNode = gridApi.getRowNode(partId);

		if (partNode) {
			gridApi.ensureNodeVisible(partNode, "middle");
			partNode.setSelected(true);
			setSelectedRowData(partNode.data);
			setShowModal("update");
		}
	}, [isLoading, gridApi, partId, rows]);

	useEffect(() => {
		registerConfig({
			id: "edgerRollContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2
				container
				justifyContent="flex-start"
				direction="column"
				p={2}
				spacing={1}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Edger Rolls
					</Typography>
				</Grid2>
				<Grid2 container size={{ md: 12 }} spacing={1} alignItems="center">
					<Grid2 size={{ xs: 12, md: 11 }}>
						<TextFieldSearchFilter
							value={filterValue}
							onChange={(val) => {
								setFilterValue(val);
								gridApi?.setGridOption("quickFilterText", val);
							}}
						/>
					</Grid2>
					<Grid2
						size={{ xs: 12, md: 1 }}
						justifyContent="flex-end"
						display="flex"
					>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>
				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							{selectedRows.length > 0 && (
								<CatalogsActionsHeader
									selectedRows={selectedRows}
									isDeletingAll={selectedRows.length == rows?.length}
									handleClearSelection={handleClearSelection}
									handleDelete={() => {
										if (selectedRows.length > 1) {
											setShowModal("deleteMultiple");
											return;
										}
										const row = selectedRows[0];
										setSelectedRowData(row);
										if (row.IsInActiveCarrier) {
											setShowModal("activeCarrier");
										} else if (row.IsInActiveRollset) {
											setShowModal("confirmRollsetDeactivation");
										} else {
											setShowModal("delete");
										}
									}}
									isLoading={false}
								/>
							)}
							<Paper
								elevation={2}
								style={{ height: "100%", width: "100%" }}
								id="edger-roll-table"
								className="ag-theme-material"
							>
								<AgGridReact<EdgerRoll>
									loading={isLoading}
									rowData={rows || []}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => String(params.data.PartId)}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									onRowClicked={onRowClicked}
									gridOptions={{
										theme: themeDXT,
										rowSelection: {
											mode: "multiRow",
											checkboxes: true,
											enableClickSelection: false,
											headerCheckbox: true,
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel
															handleNew={handleNewRoll}
															id="new-edger-roll"
														/>
													),
													align: "left",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper
								elevation={2}
								style={{ height: "100%", width: "100%", overflowY: "scroll" }}
							>
								<EdgerRollDetails
									roll={selectedRowData}
									onHide={onModalHide}
									modal={
										showModal === "deleteMultiple"
											? ""
											: (showModal as "create" | "update" | "delete" | "")
									}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error in Edger Rolls"
			/>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.PartId!,
					Name: selectedRowData?.PartName!,
					Entity: "edger roll",
					QueryKey: "edgerRoll",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={deleteEdgerRoll}
			/>
			<MultipleDeleteSelectModal
				open={showModal === "deleteMultiple"}
				onClose={(deleted: boolean) => {
					if (deleted) {
						setSelectedRows([]);
					}
					setShowModal("");
				}}
				deleteFunction={deleteEdgerRoll}
				selectedRows={selectedRows}
				entity={{
					queryKey: "edgerRoll",
					name: "Edger Roll",
				}}
			/>
			<ActiveCarrierModal
				show={showModal === "activeCarrier"}
				action="delete"
				entityName={selectedRowData?.PartName || ""}
				entityType="Edger Roll"
				actionDescription="This edger roll is in an active carrier and cannot be deleted."
				carrier={
					selectedRowData
						? {
								CarrierName: selectedRowData.CarrierName,
								CarrierStatusName: selectedRowData.CarrierStatusName,
							}
						: null
				}
				rollsetInfo={
					selectedRowData?.RollsetId
						? {
								RollsetId: selectedRowData.RollsetId,
								RollsetLetter: selectedRowData.RollsetLetter,
								RollsetSectionName: selectedRowData.RollsetSectionName,
								RollsetStandName: selectedRowData.RollsetStandName,
								RollsetTopRollName: selectedRowData.RollsetTopRollName,
								RollsetBottomRollName: selectedRowData.RollsetBottomRollName,
							}
						: null
				}
				onClose={() => {
					setShowModal("");
					setSelectedRowData(null);
					handleClearSelection();
				}}
			/>
			<RollsetDeactivationConfirmModal
				show={showModal === "confirmRollsetDeactivation"}
				entityName={selectedRowData?.PartName || ""}
				entityType="Roll"
				actionDescription="Deleting this roll"
				rollsetInfo={
					selectedRowData?.RollsetId
						? {
								RollsetId: selectedRowData.RollsetId,
								RollsetLetter: selectedRowData.RollsetLetter,
								RollsetSectionName: selectedRowData.RollsetSectionName,
								RollsetStandName: selectedRowData.RollsetStandName,
								RollsetTopRollName: selectedRowData.RollsetTopRollName,
								RollsetBottomRollName: selectedRowData.RollsetBottomRollName,
							}
						: null
				}
				onConfirm={() => {
					setShowModal("delete");
				}}
				onCancel={() => {
					setShowModal("");
					setSelectedRowData(null);
					handleClearSelection();
				}}
			/>
		</>
	);
};
